import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api, endpoints } from '@/api/client';
import { queryKeys } from '@/api/queryClient';

// ============ Types ============

export interface ScanScript {
  id: string;
  name: string;
  description: string | null;
  category: string;
  scriptContent?: string;
  outputFormat: string;
  timeoutSeconds: number;
  isBuiltIn: boolean;
  isEnabled: boolean;
  parameters: Record<string, unknown> | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScanScriptsResponse {
  data: ScanScript[];
  total: number;
  categories: string[];
}

export interface ScanJob {
  id: string;
  name: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  statusLabel: string;
  statusColor: string;
  scheduleType: string;
  devicesTotal: number;
  devicesCompleted: number;
  devicesFailed: number;
  devicesSkipped: number;
  progressPercent: number;
  isFinished: boolean;
  parameters: Record<string, unknown> | null;
  startedAt: string | null;
  completedAt: string | null;
  createdBy: string;
  createdAt: string;
  script: {
    id: string;
    name: string;
    category: string;
  };
  results?: ScanResult[];
}

export interface ScanJobsResponse {
  data: ScanJob[];
  total: number;
  summary: {
    pending: number;
    running: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
}

export interface ScanResult {
  id: string;
  status: 'success' | 'failed' | 'timeout' | 'skipped';
  statusLabel: string;
  statusColor: string;
  outputData: unknown | null;
  rawOutput: string | null;
  errorOutput: string | null;
  exitCode: number | null;
  executionTimeMs: number | null;
  executedAt: string;
  device: {
    id: string;
    hostname: string | null;
  };
}

export interface ScanResultsResponse {
  data: ScanResult[];
  total: number;
  summary: {
    success: number;
    failed: number;
    timeout: number;
    skipped: number;
  };
}

export interface CreateScriptInput {
  name: string;
  description?: string;
  scriptContent: string;
  category?: string;
  outputFormat?: string;
  timeoutSeconds?: number;
  parameters?: Record<string, unknown>;
  createdBy?: string;
}

export interface UpdateScriptInput {
  name?: string;
  description?: string;
  scriptContent?: string;
  category?: string;
  outputFormat?: string;
  timeoutSeconds?: number;
  parameters?: Record<string, unknown>;
  isEnabled?: boolean;
}

export interface CreateJobInput {
  scriptId: string;
  deviceIds: string[];
  name?: string;
  parameters?: Record<string, unknown>;
  createdBy?: string;
  autoStart?: boolean;
  checkOnlineStatus?: boolean;
}

export interface RunScriptInput {
  scriptId: string;
  deviceId: string;
  parameters?: Record<string, unknown>;
  executedBy?: string;
}

// ============ Script Hooks ============

export function useScanScripts(category?: string, includeContent = false) {
  return useQuery({
    queryKey: queryKeys.scans.scripts,
    queryFn: () =>
      api.get<ScanScriptsResponse>(endpoints.scans.scripts, {
        params: {
          ...(category ? { category } : {}),
          includeContent,
        },
      }),
  });
}

export function useScanScript(id: string) {
  return useQuery({
    queryKey: queryKeys.scans.script(id),
    queryFn: () => api.get<ScanScript>(endpoints.scans.script(id)),
    enabled: !!id,
  });
}

export function useCreateScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateScriptInput) =>
      api.post<ScanScript>(endpoints.scans.scripts, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.scripts });
      notifications.show({
        title: 'Script Created',
        message: 'PowerShell script has been created successfully',
        color: 'green',
      });
    },
  });
}

export function useUpdateScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateScriptInput & { id: string }) =>
      api.put<ScanScript>(endpoints.scans.script(id), input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.scripts });
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.script(variables.id) });
      notifications.show({
        title: 'Script Updated',
        message: 'PowerShell script has been updated successfully',
        color: 'green',
      });
    },
  });
}

export function useDeleteScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(endpoints.scans.script(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.scripts });
      notifications.show({
        title: 'Script Deleted',
        message: 'PowerShell script has been deleted',
        color: 'green',
      });
    },
  });
}

// ============ Job Hooks ============

export function useScanJobs(limit?: number) {
  return useQuery({
    queryKey: queryKeys.scans.jobs,
    queryFn: () =>
      api.get<ScanJobsResponse>(endpoints.scans.jobs, {
        params: limit ? { limit } : undefined,
      }),
  });
}

export function useRunningJobs() {
  return useQuery({
    queryKey: queryKeys.scans.jobsRunning,
    queryFn: () => api.get<{ data: ScanJob[] }>(endpoints.scans.jobsRunning),
    refetchInterval: 2000, // Poll every 2 seconds for running jobs
  });
}

export function useScanJob(id: string) {
  return useQuery({
    queryKey: queryKeys.scans.job(id),
    queryFn: () => api.get<ScanJob>(endpoints.scans.job(id)),
    enabled: !!id,
  });
}

export function useScanJobResults(id: string) {
  return useQuery({
    queryKey: queryKeys.scans.jobResults(id),
    queryFn: () => api.get<ScanResultsResponse>(endpoints.scans.jobResults(id)),
    enabled: !!id,
  });
}

export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateJobInput) =>
      api.post<ScanJob>(endpoints.scans.jobs, input),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.jobs });
      notifications.show({
        title: 'Scan Started',
        message: `Running "${job.script.name}" on ${job.devicesTotal} device(s)`,
        color: 'blue',
      });
    },
  });
}

export function useStartJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<ScanJob>(endpoints.scans.jobStart(id)),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.jobs });
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.job(job.id) });
      notifications.show({
        title: 'Scan Started',
        message: 'Scan job has been started',
        color: 'blue',
      });
    },
  });
}

export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<ScanJob>(endpoints.scans.jobCancel(id)),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.jobs });
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.job(job.id) });
      notifications.show({
        title: 'Scan Cancelled',
        message: 'Scan job has been cancelled',
        color: 'orange',
      });
    },
  });
}

// ============ Quick Run Hook ============

export function useRunScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RunScriptInput) =>
      api.post<{ success: boolean; result: ScanResult | null; error: string | null }>(
        endpoints.scans.run,
        input
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.jobs });
      if (response.success) {
        notifications.show({
          title: 'Script Executed',
          message: 'Script executed successfully',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Script Failed',
          message: response.error || 'Script execution failed',
          color: 'red',
        });
      }
    },
  });
}
